// src/component/UmmahTrendingSection.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { renderMentionsOnly } from "../../utils/textParser.tsx";
import { hideVideoLines } from "../../utils/youtube.ts";

function timeAgoFromISO(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";

  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const week = Math.floor(day / 7);
  const month = Math.floor(day / 30);

  if (month > 0) return `${month} month${month > 1 ? "s" : ""} ago`;
  if (week > 0) return `${week} week${week > 1 ? "s" : ""} ago`;
  if (day > 0) return `${day} day${day > 1 ? "s" : ""} ago`;
  if (hr > 0) return `${hr} hour${hr > 1 ? "s" : ""} ago`;
  if (min > 0) return `${min} min ago`;
  return "just now";
}

function Stat({ icon, value }) {
  if (value == null) return null;
  return (
    <div className="inline-flex items-center gap-1 text-body-sm text-text-tertiary">
      <span className="material-symbols-outlined text-[18px] leading-none">{icon}</span>
      <span>{value}</span>
    </div>
  );
}

export default function UmmahTrendingSection({
  title = "Trending in Ummah",
  posts = [],
  onViewAll,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const safe = Array.isArray(posts) ? posts.slice(0, 3) : [];
  if (safe.length === 0) return null;

  return (
    <section className="mt-10">
      <div className="rounded-2xl border border-border-tertiary bg-white">
        {/* Header */}
        <div className="px-6 pt-6 pb-2">
          <div className="text-heading-sm text-text-primary">{title}</div>
        </div>

        {/* List */}
        <div className="divide-y divide-border-tertiary">
          {safe.map((p, i) => {
            const rank = p?.index + 1 ?? i + 1;
            const tag = p?.category_name ?? null;
            const likeCount = p?.like_count ?? p?.likes ?? null;
            const commentCount = p?.comment_count ?? p?.comments ?? null;
            const viewCount = p?.view_count ?? p?.views ?? null;
            const createdAt = p?.created_at ?? p?.createdAt ?? null;
            const ago = timeAgoFromISO(createdAt);
            const thumb = p?.images?.[0] ?? null;

            return (
              <button
                key={p.uuid ?? `${i}`}
                type="button"
                onClick={() => navigate(`/community/${p.uuid}`)}
                className="w-full text-left px-6 py-4 hover:bg-bg-secondary active:opacity-95"
              >
                <div className="flex items-start gap-4">
                  {/* Rank */}
                  <div className="w-6 shrink-0 text-body-md-sb text-text-primary">{rank}</div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    {/* Tag pill */}
                    {tag ? (
                      <div className="inline-flex items-center rounded-md bg-bg-secondary px-2 py-1 text-body-sm text-text-secondary">
                        {tag}
                      </div>
                    ) : null}

                    {/* Title */}
                    <div className="mt-2 text-body-lg-sb text-text-primary line-clamp-2">
                      {p.title ?? "Untitled"}
                    </div>

                    {/* Body — 유저 태그(@[user:uuid])를 @nickname 으로 치환해 표시 */}
                    {p.body ? (
                      <div className="mt-1 text-body-md text-text-tertiary line-clamp-2">
                        {renderMentionsOnly(hideVideoLines(p.body), p.mentions_map ?? {})}
                      </div>
                    ) : null}

                    {/* Read more */}
                    <div className="mt-2 text-body-md-sb text-text-primary underline underline-offset-2">
                      Read more
                    </div>
                  </div>

                  {/* Optional thumbnail */}
                  {thumb ? (
                    <div className="shrink-0 w-[64px] h-[64px] rounded-lg overflow-hidden border border-border-tertiary bg-bg-secondary">
                      <img
                        src={thumb}
                        alt={p?.title ?? "post"}
                        className="w-full h-full object-cover block"
                        loading="lazy"
                      />
                    </div>
                  ) : null}
                </div>
                {/* Stats + time */}
                <div className="mt-3 flex items-center justify-between gap-3 pl-10">
                  <div className="flex items-center gap-4">
                    <Stat icon="favorite_border" value={likeCount} />
                    <Stat icon="chat_bubble_outline" value={commentCount} />
                    <Stat icon="visibility" value={viewCount} />
                  </div>

                  {ago ? (
                    <div className="text-body-sm text-text-tertiary">{ago}</div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer button */}
        <div className="p-6">
          <button
            type="button"
            onClick={onViewAll ?? (() => navigate("/community"))}
            className="w-full rounded-xl border border-border-tertiary bg-white px-4 py-4
              text-body-md-sb text-text-secondary hover:bg-bg-secondary"
          >
            {t("home.view_all_posts")}
          </button>
        </div>
      </div>
    </section>
  );
}
